/**
 * The searchable text of a tool / capability, and the matcher over it.
 *
 * Both list surfaces used to search `capabilityId + path` (catalog) or
 * `name + method + path` (tools) — which excluded the **description**, the only
 * human-language field on the object. A non-developer searching "refund" or
 * "cancel" found a tool only when its machine id happened to contain the word
 * (plan F-28). On a surface whose whole purpose is to be legible to someone who
 * does not read HTTP verbs, that is the primary find failure.
 *
 * Shared by both surfaces so they can never drift apart again.
 */

/** The fields a row contributes to its haystack. Every one is optional. */
export interface SearchableRow {
  /** Human name (an override or an enriched name). */
  readonly name?: string | null;
  /** Human title, when the producer distinguishes it from `name`. */
  readonly title?: string | null;
  /** The human description — the field that was missing. */
  readonly description?: string | null;
  /** Stable machine id (`orders.cancel`). */
  readonly capabilityId?: string | null;
  /** HTTP method + path. */
  readonly method?: string | null;
  readonly path?: string | null;
  /** Business area / domain. */
  readonly domain?: string | null;
  /** Originating service, on a multi-service catalog. */
  readonly service?: string | null;
}

/**
 * Case-folded, diacritic-stripped comparison key.
 *
 * Diacritic folding matters here beyond tidiness: an operator working in a
 * non-English locale types "área" for a domain the catalog stores as "area",
 * and a strict comparison silently finds nothing.
 */
export function normalizeSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/** Every searchable field of a row, normalized and joined. */
export function buildHaystack(row: SearchableRow): string {
  return [
    row.name,
    row.title,
    row.description,
    row.capabilityId,
    row.method,
    row.path,
    row.domain,
    row.service,
  ]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .map(normalizeSearch)
    .join(' ');
}

/**
 * Does `row` match `query`?
 *
 * Every whitespace-separated term must appear (AND), so "cancel order" finds
 * "Cancel an order" without also matching every row containing either word.
 * Matching is substring-based, which makes a partial word ("canc") find its row
 * — the behaviour someone typing expects from a filter box.
 *
 * An empty / whitespace-only query matches everything.
 */
export function matchesSearch(row: SearchableRow, query: string): boolean {
  return matchesHaystack(buildHaystack(row), query);
}

/**
 * The same match, for a caller that already holds a normalized haystack.
 *
 * The settings search builds its haystack once per entry per locale rather than
 * per keystroke — it is a localized label plus a breadcrumb plus a keyword bag,
 * none of which changes while somebody types. Splitting the rule out is what
 * keeps the two surfaces honest: the top-bar field and the ⌘K palette run this
 * exact function, so a result one of them offers is a result the other offers.
 *
 * `haystack` must already be `normalizeSearch`d; the query is normalized here.
 */
export function matchesHaystack(haystack: string, query: string): boolean {
  const terms = normalizeSearch(query)
    .split(/\s+/)
    .filter((term) => term.length > 0);
  if (terms.length === 0) return true;
  return terms.every((term) => haystack.includes(term));
}
